import { useWasDetailAdapter } from '@/application/adapter/wasDetail.adapter';
import {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
} from '@/common/types/slideDetailAdapter.types';
import {
  KubernetesPanelInfo,
  useKubernetesSlideDetailAdapter,
} from '@/kubernetes/adapter/kubernetesSlideDetail.adapter';
import { useOpenDatabaseDetail } from '@/database/adapter';
import { useHostDetailAdapter } from '@/infrastructure/adapter/hostDetail.adapter';
import { useGpuDetailAdapter } from '@/gpu/adapter/gpuDetail.adapter';
import { useNetworkDeviceDetailAdapter } from '@/infrastructure/adapter/networkDeviceDetail.adapter';
import { useNetworkObjectDetailAdapter } from '@/infrastructure/adapter/networkObjectDetail.adapter';
import { useKafkaDetailAdapter } from '@/messageQueue/adapter/kafkaDetail.adapter';
import { PanelInfo, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { KafkaInstanceType } from '@/messageQueue/utils/kafka/types';
import { KAFKA_INSTANCE_TYPE } from '@/messageQueue/utils/kafka/define';
import { useCloudDetailAdapter } from '@/cloud/adapter/cloudDetail.adapter';
import { useServiceListAdapter } from '@/service/adapter/serviceList.adapter';
import { useUrlMonitoringAdapter } from '@/application/adapter/urlMonitoring.adapter';
import { useWebServerDetailAdapter } from '@/application/adapter/webServerDetail.adapter';
import { Category } from '@/ai/utils/types';
import { FRAME_NAMES } from '../define/apiTrace.define';
import { getV7InfraContainersInfoByContainerId } from '@/api-client/infra/queries/infra-container-v7-controller/infra-container-v7-controller';
import { useWebApplicationAdapter } from '@/application/adapter/webApplication.adapter';

type OpenDetailCategory = Category | 'rum';

const isApplicationCategory = (category: OpenDetailCategory) => {
  return ['java', 'dotnet', 'python', 'nodejs', 'application'].includes(category);
};

const isDatabaseCategory = (category: OpenDetailCategory) => {
  return [
    'oracle',
    'postgresql',
    'mysql',
    'redis',
    'sqlserver',
    'mongodb',
    'tibero',
    'altibase',
    'cubrid',
    'database_cluster',
    'database',
  ].includes(category);
};

const isCloudCategory = (category: OpenDetailCategory) => {
  return [
    'cloud',
    'aws_account',
    'aws_aurora',
    'aws_dynamodb',
    'aws_redshift',
    'aws_documentdb',
    'aws_elasticache_cluster',
    'aws_elasticache_node',
    'aws_elasticache_serverless',
    'aws_elasticache_shard',
    'aws_ec2',
    'aws_lambda',
    'aws_lightsail_database',
    'aws_lightsail_instance',
    'aws_rds',
    'aws_s3',
    'aws_vpc',
    'aws_dms_replinstance',
    'aws_dms_task',
    'azure_virtual_machines',
    'azure_functions',
    'azure_blob_storage',
    'azure_cosmos_db',
    'azure_cache_for_redis',
    'azure_sql_database',
    'azure_database_for_postgresql',
    'azure_database_for_mysql',
  ].includes(category);
};

const isHostCategory = (category: OpenDetailCategory) => {
  return ['host', 'host_port', 'filesystem'].includes(category);
};

const KUBERNETES_DETAIL_TYPE_MAP: Partial<Record<Category, KubernetesPanelInfo['type']>> = {
  namespace: TOTAL_DETAIL.NAMESPACE,
  storage_class: TOTAL_DETAIL.NAMESPACE,
  ingress: TOTAL_DETAIL.NAMESPACE,
  role: TOTAL_DETAIL.NAMESPACE,
  pod: TOTAL_DETAIL.POD,
  node: TOTAL_DETAIL.NODE,
  hpa: TOTAL_DETAIL.HPA,
  persistent_volume: TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS,
  persistent_volume_claim: TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS,
  statefulset: TOTAL_DETAIL.STATEFUL_SET,
  job: TOTAL_DETAIL.JOB,
  deployment: TOTAL_DETAIL.DEPLOYMENT,
  daemonset: TOTAL_DETAIL.DAEMON_SET,
  cronjob: TOTAL_DETAIL.CRON_JOB,
  cluster: TOTAL_DETAIL.NODE_LIST,
  cluster_role: TOTAL_DETAIL.CLUSTER_ROLES,
  cluster_role_binding: TOTAL_DETAIL.CLUSTER_ROLES_BINDINGS,
  service_account: TOTAL_DETAIL.SERVICE_ACCOUNTS,
  replicaset: TOTAL_DETAIL.REPLICA_SET,
  configmap: TOTAL_DETAIL.CONFIG_MAPS,
  role_binding: TOTAL_DETAIL.ROLES_BINDINGS,
  route: TOTAL_DETAIL.OPENSHIFT_ROUTE,
  service: TOTAL_DETAIL.SERVICE,
};

const KAFKA_INSTANCE_TYPE_MAP: Partial<Record<Category, KafkaInstanceType>> = {
  kafka_cluster: KAFKA_INSTANCE_TYPE.CLUSTER,
  kafka_zookeeper: KAFKA_INSTANCE_TYPE.CLUSTER,
  kafka_broker: KAFKA_INSTANCE_TYPE.BROKER,
  kafka_cluster_topic: KAFKA_INSTANCE_TYPE.TOPIC,
  kafka_broker_topic: KAFKA_INSTANCE_TYPE.TOPIC,
};

export const useDetailAdapterByCategory = () => {
  const kubernetesAdapter = useKubernetesSlideDetailAdapter();
  const wasAdapter = useWasDetailAdapter();
  const databaseAdapter = useOpenDatabaseDetail();
  const hostAdapter = useHostDetailAdapter();
  const gpuAdapter = useGpuDetailAdapter();
  const networkDeviceAdapter = useNetworkDeviceDetailAdapter();
  const networkObjectAdapter = useNetworkObjectDetailAdapter();
  const kafkaAdapter = useKafkaDetailAdapter();
  const cloudAdapter = useCloudDetailAdapter();
  const serviceListAdapter = useServiceListAdapter();
  const urlMonitoringAdapter = useUrlMonitoringAdapter();
  const webServerAdapter = useWebServerDetailAdapter();
  const webApplicationAdapter = useWebApplicationAdapter();

  const openKubernetesSlideDetail = (
    params: SlideDetailAdapterParams,
    type: KubernetesPanelInfo['type'],
    additionKey?: string,
  ): Promise<SlideDetailAdapterResult> => {
    const fromTime = params.params?.fromTime ? params.params?.fromTime?.toString() : undefined;
    const toTime = params.params?.toTime ? params.params?.toTime?.toString() : undefined;
    return kubernetesAdapter.openKubernetesSlideDetail({
      ...params,
      params: {
        type,
        name: params.params?.targetName?.toString() ?? '',
        addition: {
          [additionKey ?? 'uid']: params.params?.targetId?.toString() ?? '',
        },
        fromTime,
        toTime,
      },
    });
  };

  const openContainerDetail = async (params: SlideDetailAdapterParams) => {
    const targetId = params.params?.targetId?.toString() ?? '';
    const targetName = params.params?.targetName?.toString() ?? '';
    const { data } = await getV7InfraContainersInfoByContainerId(targetId, undefined, {
      frameName: FRAME_NAMES.CONTAINER_DETAIL.INFORMATION,
    });
    const { server } = data?.data?.[0] ?? {};
    const { podUid, containerName: k8sName } = data?.data?.[0]?.kubeContainer ?? {};
    const { containerName, hostId } = data?.data?.[0]?.containerHost ?? {};
    const fromTime = params.params?.fromTime ? params.params?.fromTime?.toString() : undefined;
    const toTime = params.params?.toTime ? params.params?.toTime?.toString() : undefined;
    const addition: PanelInfo<
      typeof TOTAL_DETAIL.K8S_CONTAINER | typeof TOTAL_DETAIL.CONTAINER_HOST
    >['addition'] =
      server === 'k8s'
        ? { server: 'k8s', podUid: podUid ?? '' }
        : { server: 'container_host', containerHostId: hostId ?? '' };

    return kubernetesAdapter.openKubernetesSlideDetail({
      ...params,
      params: {
        type: server === 'k8s' ? TOTAL_DETAIL.K8S_CONTAINER : TOTAL_DETAIL.CONTAINER_HOST,
        name: targetName || k8sName || containerName || '',
        container: targetId,
        addition,
        fromTime,
        toTime,
      },
    });
  };

  const openKafkaDetail = (params: SlideDetailAdapterParams, instanceType: KafkaInstanceType) => {
    return kafkaAdapter.openKafkaDetail({
      ...params,
      params: {
        ...params.params,
        instanceId: params.params?.targetId?.toString() ?? '',
        instanceName: params.params?.targetName?.toString() ?? '',
        instanceType,
      },
    });
  };

  const openCloudDetail = (params: SlideDetailAdapterParams) =>
    cloudAdapter.openDetail({
      ...params,
      params: {
        ...params.params,
        targetId: params.params?.targetId?.toString() ?? '',
      },
    });

  const openDatabaseDetail = async (params: SlideDetailAdapterParams) => {
    const instanceId = params.params?.targetId?.toString() ?? '';
    const instanceName = params.params?.targetName?.toString() ?? '';
    const fromTime = params.params?.fromTime ? params.params?.fromTime?.toString() : undefined;
    const toTime = params.params?.toTime ? params.params?.toTime?.toString() : undefined;

    return databaseAdapter.openDatabaseDetailDashboard({
      ...params,
      params: { instanceId, instanceName, infoObj: { instanceName }, fromTime, toTime },
    });
  };

  const openNetworkInterfaceDetail = async (params: SlideDetailAdapterParams) => {
    const targetId = params.params?.targetId?.toString() ?? '';
    return networkDeviceAdapter.openNetworkDeviceDetail({
      ...params,
      params: { ...params.params, targetId, selectedTab: 'interface' },
    });
  };

  // 알람 대상 targetId는 "serverId|gpuUuid"(호스트) 또는 "gpuUuid"(노드) 형식 → 순수 gpuUuid만 추출해 트리거 진입.
  // serverId/serverType·부모 GPU는 어댑터가 mig-info API로 resolve (대시보드 gpuInfo 경로와 동일).
  const openGpuTriggerDetail = async (params: SlideDetailAdapterParams) => {
    const rawTargetId = params.params?.targetId?.toString() ?? '';
    const gpuUuid = rawTargetId.includes('|') ? rawTargetId.split('|')[1] : rawTargetId;
    return gpuAdapter.openGpuDetailByTrigger({
      ...params,
      params: {
        gpuUuid,
        name: params.params?.targetName?.toString(),
        fromTime: params.params?.fromTime?.toString(),
        toTime: params.params?.toTime?.toString(),
      },
    });
  };

  // 알람 MIG 대상 targetId는 migUniqueUuid(HOST-MIG-.../NODE-MIG-...) → migUuid로 트리거 진입.
  // GPU와 동일하게 mig-info API가 부모 GPU(serverId/serverType/gpuUniqueUuid)를 resolve하고, 해당 MIG를 선택 상태로 연다.
  const openMigTriggerDetail = async (params: SlideDetailAdapterParams) => {
    const rawTargetId = params.params?.targetId?.toString() ?? '';
    const migUuid = rawTargetId.includes('|') ? rawTargetId.split('|')[1] : rawTargetId;
    return gpuAdapter.openGpuDetailByTrigger({
      ...params,
      params: {
        migUuid,
        name: params.params?.targetName?.toString(),
        fromTime: params.params?.fromTime?.toString(),
        toTime: params.params?.toTime?.toString(),
      },
    });
  };

  const openDetailByCategory = (
    category: OpenDetailCategory,
    params: SlideDetailAdapterParams,
  ): Promise<SlideDetailAdapterResult> => {
    const kubernetesType = KUBERNETES_DETAIL_TYPE_MAP[category];
    if (kubernetesType) return openKubernetesSlideDetail(params, kubernetesType);

    const kafkaType = KAFKA_INSTANCE_TYPE_MAP[category];
    if (kafkaType) return openKafkaDetail(params, kafkaType);

    if (isApplicationCategory(category)) return wasAdapter.openWasDetail(params);
    if (isDatabaseCategory(category)) return openDatabaseDetail(params);
    if (isHostCategory(category)) return hostAdapter.openHostDetail(params);
    if (isCloudCategory(category)) return openCloudDetail(params);

    switch (category) {
      case 'process':
      case 'processDetailInfo':
        return hostAdapter.openHostProcessDetail(params);
      case 'container':
        return openContainerDetail(params);
      case 'gpu':
        return openGpuTriggerDetail(params);
      case 'mig':
        return openMigTriggerDetail(params);
      case 'network_device':
        return networkDeviceAdapter.openNetworkDeviceDetail(params);
      case 'interface':
        return openNetworkInterfaceDetail(params);
      case 'network_object':
        return networkObjectAdapter.openNetworkObjectDetail(params);
      case 'business_service':
        return serviceListAdapter.openServiceListDetail(params);
      case 'url':
        return urlMonitoringAdapter.openUrlMonitoringDetail(params);
      case 'webserver':
        return webServerAdapter.openWebServerDetail(params);
      case 'rum':
        return webApplicationAdapter.openWebApplicationDetail(params);
      default:
        return Promise.resolve({
          opened: false,
          reason: 'invalid_detail_type',
        });
    }
  };

  return {
    openDetailByCategory,
  };
};
